/**
 * BYOK provider key management (AB#669). Endpoints all live under `/api/v1/ai`
 * (JWT required), not nested under a site — a key's scope is account/site/user,
 * so it's addressed the same way regardless of which site you're looking from.
 *
 * Key safety is non-negotiable server-side: no endpoint ever returns a
 * decrypted key, so there is no `reveal` method here — masked rows only.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** Every provider WordPuppi can proxy Baxter through. */
export type AiProviderId =
  | 'openrouter'
  | 'openai'
  | 'anthropic'
  | 'deepseek'
  | 'zai'
  | 'moonshot'
  | 'qwen'
  | 'copilot';

/** Where a key lives — derived server-side from user_id/site_id, never chosen directly. */
export type AiKeyScope = 'user' | 'site' | 'account';

/** Registry order + display labels — shared by the Baxter model picker and the Account page. */
export const AI_PROVIDERS: { id: AiProviderId; label: string }[] = [
  { id: 'openrouter', label: 'OpenRouter' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'zai', label: 'Z.ai' },
  { id: 'moonshot', label: 'Moonshot' },
  { id: 'qwen', label: 'Qwen' },
  { id: 'copilot', label: 'GitHub Copilot' },
];

/** Human label for a provider id — falls back to the raw id for forward-compat. */
export function aiProviderLabel(id: string): string {
  return AI_PROVIDERS.find((p) => p.id === id)?.label ?? id;
}

/** A masked BYOK key row. */
export interface AiProviderKey {
  pid: string;
  provider: AiProviderId;
  label: string;
  /** Last 4 chars only. */
  key_hint: string;
  scope: AiKeyScope;
  /** Meaningless on `scope: 'user'` — the server rejects it there. */
  is_shared: boolean;
  /** Present on `site`-scoped rows, for display. */
  site_slug?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateAiProviderKeyRequest {
  provider: AiProviderId;
  label: string;
  key: string;
  scope: AiKeyScope;
  /** Required when scope is 'site'. */
  site_slug?: string;
  /** Ignored (and rejected) on `scope: 'user'`. */
  is_shared?: boolean;
}

export interface UpdateAiProviderKeyRequest {
  label?: string;
  is_shared?: boolean;
  /** Replace the stored key — re-validated the same way as create. */
  key?: string;
}

export const aiKeysApi = {
  /**
   * Visible masked rows for the caller: their own personal keys, shared
   * site/account keys, and (if they own the account) its unshared keys too.
   * `site_slug` resolves which site's shared keys are in view.
   */
  list: (siteSlug?: string) =>
    api.get<ApiResponse<AiProviderKey[]>>(
      siteSlug ? `/v1/ai/keys?site_slug=${encodeURIComponent(siteSlug)}` : '/v1/ai/keys',
    ),

  /** Create a key. 400 `invalid_provider_key` if the provider rejects it. */
  create: (data: CreateAiProviderKeyRequest) =>
    api.post<ApiResponse<AiProviderKey>>('/v1/ai/keys', data),

  /** Update label / is_shared, or replace the key itself. */
  update: (pid: string, data: UpdateAiProviderKeyRequest) =>
    api.patch<ApiResponse<AiProviderKey>>(`/v1/ai/keys/${pid}`, data),

  /** Delete a key. */
  delete: (pid: string) => api.delete<ApiResponse<null>>(`/v1/ai/keys/${pid}`),
};
